// Heap / Priority Queue — curated interview problems with multiple worked approaches.
// Same shape as dsaProblems/tries.js: concise steps, a "why & when to use" close per
// approach, and an interactive visualizer wherever it clarifies the heap's shape.

const heapProblems = [
  {
    id: 'kth-largest-element-in-an-array',
    title: 'Kth Largest Element in an Array',
    difficulty: 'Medium',
    leetcode: { number: 215, url: 'https://leetcode.com/problems/kth-largest-element-in-an-array/' },
    statement: 'Given an integer array nums and an integer k, return the kth largest element — the kth largest in SORTED ORDER, not the kth distinct value.',
    example: {
      input: 'nums = [3, 2, 1, 5, 6, 4], k = 2',
      output: '5',
      note: 'Sorted descending: 6, 5, 4, 3, 2, 1 — the 2nd largest is 5.',
    },
    approaches: [
      {
        name: 'Approach 1 — Brute Force (Sort, Then Index In)',
        why:
          "Sort the entire array in descending order, and read off the element at index k − 1. Direct and " +
          "correct, but fully sorting the array is more work than finding just one specific rank requires.",
        steps: [
          'Sort the array in descending order.',
          'Return the element at index k − 1.',
          'Correct, but the entire array gets sorted even though only one position is actually needed.',
        ],
        code:
`int findKthLargestBruteForce(int[] nums, int k) {
    Integer[] boxed = Arrays.stream(nums).boxed().toArray(Integer[]::new);
    Arrays.sort(boxed, Collections.reverseOrder());
    return boxed[k - 1];
}`,
        complexity: { time: 'O(n log n)', space: 'O(n) — boxing for the comparator sort' },
        whenToUse: "Only useful to confirm what \"kth largest\" means — full sorting to answer a single-rank question is exactly what both optimized approaches below avoid.",
      },
      {
        name: 'Approach 2 — Optimized: A Min-Heap of Size K',
        why:
          "Keep a min-heap capped at size k — the k LARGEST values seen so far, with the smallest of THOSE " +
          "always on top. Once the heap holds k elements, any new value smaller than the heap's current minimum " +
          "can't possibly be in the top k, and gets discarded without a second thought.",
        steps: [
          'Push each number onto a min-heap.',
          'If the heap now holds more than k elements, remove the smallest (the root).',
          'After every number is processed, the heap holds exactly the k largest values.',
          'The root of that heap is the smallest among them — exactly the kth largest overall.',
        ],
        visual: {
          arrays: [{ label: 'nums (k = 2)', values: [3, 2, 1, 5, 6, 4] }, { label: 'heap (min, size ≤ 2)', values: [] }],
          varNames: [],
          steps: [
            { highlights: { nums: [0] }, snapshots: { 'heap (min, size ≤ 2)': [3] }, note: 'Push 3. Heap: [3].' },
            { highlights: { nums: [1] }, snapshots: { 'heap (min, size ≤ 2)': [2, 3] }, note: 'Push 2. Heap: [2, 3] (size 2, at the cap).' },
            { highlights: { nums: [2] }, snapshots: { 'heap (min, size ≤ 2)': [2, 3] }, note: 'Push 1 — heap grows to size 3, over the cap. Remove the smallest (1). Heap stays [2, 3].' },
            { highlights: { nums: [3] }, snapshots: { 'heap (min, size ≤ 2)': [3, 5] }, note: 'Push 5 — size 3 again, remove smallest (2). Heap: [3, 5].' },
            { highlights: { nums: [4] }, snapshots: { 'heap (min, size ≤ 2)': [5, 6] }, note: 'Push 6 — remove smallest (3). Heap: [5, 6].' },
            { highlights: { nums: [5] }, snapshots: { 'heap (min, size ≤ 2)': [5, 6] }, note: 'Push 4 — heap grows to 3, remove smallest (4) right back out. Heap stays [5, 6]. Root: 5 — the 2nd largest.' },
          ],
        },
        code:
`int findKthLargest(int[] nums, int k) {
    PriorityQueue<Integer> minHeap = new PriorityQueue<>();
    for (int num : nums) {
        minHeap.add(num);
        if (minHeap.size() > k) {
            minHeap.poll();
        }
    }
    return minHeap.peek();
}`,
        complexity: { time: 'O(n log k)', space: 'O(k)' },
        interviewNote: "Try this exact algorithm in the \"Kth Largest Tracker\" mode of this pattern's interactive playground — it's also the standard technique for the STREAMING version of this problem (LeetCode 703), where numbers keep arriving over time.",
        whenToUse: "Reach for this when k is small relative to n, or when the input arrives as a stream rather than all at once — the heap never holds more than k elements regardless of how large n grows.",
      },
      {
        name: 'Approach 3 — Optimized: Quickselect',
        why:
          "Borrowed from quicksort's partition step: pick a pivot, partition the array so everything smaller " +
          "ends up left of it and everything bigger ends up right, then RECURSE INTO ONLY THE HALF that must " +
          "contain the target rank — never both halves, the way full quicksort would.",
        steps: [
          'Convert \"kth largest\" into \"the element at index n − k in the sorted-ascending array.\"',
          'Pick a pivot (commonly the last element) and partition the array around it, exactly like quicksort.',
          'If the pivot landed exactly at the target index, that\'s the answer.',
          'Otherwise, recurse into only the side (left or right of the pivot) that still contains the target index.',
        ],
        visual: {
          arrays: [{ label: 'nums (target index = 4)', values: [3, 2, 1, 5, 6, 4] }],
          varNames: [],
          steps: [
            { highlights: { nums: { primary: [0, 1, 2], secondary: [5] } }, note: 'Partition around pivot nums[5]=4: 3, 2, 1 are all smaller — they stay left. 5 and 6 are bigger — they end up right.' },
            { snapshots: { nums: [3, 2, 1, 4, 6, 5] }, highlights: { nums: [3] }, note: 'Pivot (4) lands at index 3. Target is index 4 — pivot landed too early, recurse into the right side [4, 5].' },
            { snapshots: { nums: [3, 2, 1, 4, 5, 6] }, highlights: { nums: [4] }, note: 'Partition [6, 5] around pivot nums[5]=5: 6 is bigger, stays right of 5. Pivot (5) lands at index 4 — exactly the target! Answer: 5.' },
          ],
        },
        code:
`int findKthLargest(int[] nums, int k) {
    return quickSelect(nums, 0, nums.length - 1, nums.length - k);
}

private int quickSelect(int[] nums, int lo, int hi, int targetIndex) {
    int pivot = nums[hi];
    int p = lo;
    for (int i = lo; i < hi; i++) {
        if (nums[i] < pivot) {
            swap(nums, i, p);
            p++;
        }
    }
    swap(nums, p, hi);

    if (p == targetIndex) return nums[p];
    return (p < targetIndex) ? quickSelect(nums, p + 1, hi, targetIndex) : quickSelect(nums, lo, p - 1, targetIndex);
}

private void swap(int[] nums, int i, int j) {
    int temp = nums[i]; nums[i] = nums[j]; nums[j] = temp;
}`,
        complexity: { time: 'O(n) average, O(n²) worst case (rare with a randomized pivot)', space: 'O(1) extra — partitions in place' },
        interviewNote: "Worth naming the trade-off explicitly: quickselect beats the heap's O(n log k) on average, but its worst case is worse, and it mutates the input array in place — the heap approach is the safer, more predictable default unless average-case speed is specifically the ask.",
        whenToUse: "The strongest possible answer when the entire array is available upfront (not streaming) and mutating it in place is acceptable — this is the fastest average-case approach among all three.",
      },
    ],
  },
  {
    id: 'find-median-from-data-stream',
    title: 'Find Median From a Data Stream',
    difficulty: 'Hard',
    leetcode: { number: 295, url: 'https://leetcode.com/problems/find-median-from-data-stream/' },
    statement:
      'Design a structure that supports adding integers from a stream one at a time, and returning the median ' +
      'of all numbers seen so far at any point.',
    example: {
      input: 'addNum(1); addNum(2); findMedian(); addNum(3); findMedian()',
      output: '1.5, then 2',
      note: 'After [1, 2]: median = (1+2)/2 = 1.5. After [1, 2, 3]: median = 2 (the middle value).',
    },
    approaches: [
      {
        name: 'Approach 1 — Brute Force (Keep a Sorted List)',
        why:
          "Maintain the numbers seen so far in a fully sorted list. Every new number gets inserted at its " +
          "correct sorted position. The median is then trivial to read off directly from the middle. Correct, " +
          "but inserting into the middle of a sorted array requires shifting every element after it.",
        steps: [
          'Keep a list that\'s always fully sorted.',
          'For each new number, find its correct sorted position and insert it there, shifting later elements over.',
          'To find the median, read the middle element (or average the two middle elements if the count is even).',
          'Each insertion costs O(n) for the shift, even though finding the POSITION to insert at can be done in O(log n).',
        ],
        code:
`class MedianFinderBruteForce {
    private List<Integer> sorted = new ArrayList<>();

    public void addNum(int num) {
        int pos = Collections.binarySearch(sorted, num);
        if (pos < 0) pos = -(pos + 1);
        sorted.add(pos, num);   // O(n) shift
    }

    public double findMedian() {
        int n = sorted.size();
        if (n % 2 == 1) return sorted.get(n / 2);
        return (sorted.get(n / 2 - 1) + sorted.get(n / 2)) / 2.0;
    }
}`,
        complexity: { time: 'O(n) per addNum, O(1) per findMedian', space: 'O(n)' },
        whenToUse: "Only useful to confirm the median definition out loud — the O(n) shift on every insertion is exactly what the two-heap approach avoids.",
      },
      {
        name: 'Approach 2 — Optimized: Two Heaps, Balanced',
        why:
          "Split the stream into two halves: a MAX-heap holding the smaller (lower) half of numbers, and a " +
          "MIN-heap holding the bigger (upper) half. Keep the two heaps balanced in size (differing by at most " +
          "one). The median is then always immediately available: either the top of whichever heap has one " +
          "extra element, or the average of both tops if they're equal in size.",
        steps: [
          'Add the new number to the max-heap (the lower half).',
          'Move the max-heap\'s top into the min-heap, to guarantee every value in the lower half is ≤ every value in the upper half.',
          'If the min-heap now has more elements than the max-heap, move its top back to the max-heap to restore balance.',
          'findMedian(): if sizes are equal, average both heaps\' tops. Otherwise, return the top of whichever heap has one more element.',
        ],
        visual: {
          arrays: [{ label: 'maxHeap (lower half)', values: [] }, { label: 'minHeap (upper half)', values: [] }],
          varNames: [],
          steps: [
            { snapshots: { 'maxHeap (lower half)': [1] }, note: 'addNum(1): goes to maxHeap. maxHeap=[1], minHeap=[]. Sizes: 1 vs 0 — maxHeap has one extra, which is allowed.' },
            { snapshots: { 'maxHeap (lower half)': [1], 'minHeap (upper half)': [2] }, note: 'addNum(2): 2 ≥ maxHeap\'s top (1), so it goes to minHeap. maxHeap=[1], minHeap=[2]. Balanced (equal sizes). findMedian() = (1+2)/2 = 1.5.' },
            { snapshots: { 'maxHeap (lower half)': [1], 'minHeap (upper half)': [2, 3] }, note: 'addNum(3): 3 ≥ maxHeap\'s top, goes to minHeap. minHeap=[2,3] now has 2 elements vs maxHeap\'s 1 — imbalanced (min has 2 more than allowed).' },
            { snapshots: { 'maxHeap (lower half)': [1, 2], 'minHeap (upper half)': [3] }, note: 'Rebalance: move minHeap\'s top (2) over to maxHeap. maxHeap=[2,1] (root 2), minHeap=[3]. maxHeap now has 1 extra — balanced again. findMedian() = maxHeap\'s top = 2.' },
          ],
        },
        code:
`class MedianFinder {
    private PriorityQueue<Integer> maxHeap = new PriorityQueue<>(Collections.reverseOrder());  // lower half
    private PriorityQueue<Integer> minHeap = new PriorityQueue<>();                              // upper half

    public void addNum(int num) {
        maxHeap.add(num);
        minHeap.add(maxHeap.poll());          // guarantee lower half's max <= upper half's min
        if (minHeap.size() > maxHeap.size()) {
            maxHeap.add(minHeap.poll());       // rebalance if the upper half grew too big
        }
    }

    public double findMedian() {
        if (maxHeap.size() > minHeap.size()) return maxHeap.peek();
        return (maxHeap.peek() + minHeap.peek()) / 2.0;
    }
}`,
        complexity: { time: 'O(log n) per addNum, O(1) per findMedian', space: 'O(n)' },
        interviewNote: "The key invariant worth stating clearly: maxHeap always holds either the same number of elements as minHeap, or exactly one more — never fewer, and never more than one extra. That single rule is what makes findMedian() O(1).",
        whenToUse: "This two-heap balancing technique is the standard answer for ANY running-statistic-over-a-stream problem where the statistic depends on relative rank (median, a percentile) rather than a simple running sum or count.",
      },
    ],
  },
];

export default heapProblems;
